/**
 * useCampaignsV2State - Custom hook for Email Campaigns V2 module
 * Enhanced features: wizard, mail merge, CSV import, throttled sending
 */
import { useState, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../../utils/apiUrl';

export const useCampaignsV2State = ({ setError, setSuccess }) => {
  // Campaigns state
  const [campaignsV2, setCampaignsV2] = useState([]);
  const [loadingCampaignsV2, setLoadingCampaignsV2] = useState(false);
  const [selectedCampaignId, setSelectedCampaignId] = useState(null);
  
  // View state: 'list', 'wizard', 'review', 'analytics', 'suppression'
  const [campaignV2View, setCampaignV2View] = useState('list');
  
  // Suppression state
  const [suppressionList, setSuppressionList] = useState([]);
  const [suppressionStats, setSuppressionStats] = useState({});
  
  // Sending state
  const [sendingCampaignV2, setSendingCampaignV2] = useState(false);
  const [sendConfirmPhrase, setSendConfirmPhrase] = useState('');
  
  // Fetch campaigns
  const fetchCampaignsV2 = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/email-v2/campaigns`);
      setCampaignsV2(response.data || []);
    } catch (err) {
      console.error('Error fetching V2 campaigns:', err);
    }
  }, []);
  
  // Fetch suppression list
  const fetchSuppressionList = useCallback(async (search = '', reason = '') => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (reason) params.append('reason', reason);
      
      const response = await axios.get(`${API_URL}/api/email-v2/suppression?${params}`);
      setSuppressionList(response.data.suppressions || []);
      setSuppressionStats(response.data.stats || {});
    } catch (err) {
      console.error('Error fetching suppression list:', err);
    }
  }, []);
  
  // Load all data
  const loadCampaignsV2Data = useCallback(async () => {
    setLoadingCampaignsV2(true);
    try {
      await fetchCampaignsV2();
    } finally {
      setLoadingCampaignsV2(false);
    }
  }, [fetchCampaignsV2]);
  
  // Create new campaign
  const handleCreateCampaignV2 = useCallback(async () => {
    try {
      const response = await axios.post(`${API_URL}/api/email-v2/campaigns`, {
        name: 'New Campaign',
        subject: '',
        htmlBody: '<p>Start writing your email...</p>'
      });
      setSelectedCampaignId(response.data.id);
      setCampaignV2View('wizard');
      return response.data;
    } catch (err) {
      console.error('Error creating campaign:', err);
      alert('Failed to create campaign: ' + (err.response?.data?.detail || err.message));
      return null;
    }
  }, []);
  
  // Edit campaign
  const handleEditCampaignV2 = useCallback((campaignId) => {
    setSelectedCampaignId(campaignId);
    setCampaignV2View('wizard');
  }, []);
  
  // Delete campaign
  const handleDeleteCampaignV2 = useCallback(async (campaignId) => {
    if (!window.confirm('Delete this campaign?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/email-v2/campaigns/${campaignId}`);
      setCampaignsV2(prev => prev.filter(c => c.id !== campaignId));
    } catch (err) {
      console.error('Error deleting campaign:', err);
      alert('Failed to delete: ' + (err.response?.data?.detail || err.message));
    }
  }, []);
  
  // Go to review page
  const handleReviewCampaignV2 = useCallback((campaignId) => {
    setSelectedCampaignId(campaignId);
    setCampaignV2View('review');
  }, []);
  
  // Send campaign
  const handleSendCampaignV2 = useCallback(async (campaignId) => {
    if (sendConfirmPhrase !== 'SEND') {
      alert('Please type SEND to confirm');
      return;
    }
    
    setSendingCampaignV2(true);
    try {
      const response = await axios.post(`${API_URL}/api/email-v2/campaigns/${campaignId}/send`, {
        confirmationPhrase: 'SEND'
      });
      
      alert(`✅ ${response.data.message}\n\nSending to ${response.data.recipientCount} recipients.`);
      setSendConfirmPhrase('');
      setCampaignV2View('list');
      await fetchCampaignsV2();
      
      // Start polling for status updates
      pollCampaignStatus(campaignId);
      
    } catch (err) {
      console.error('Error sending campaign:', err);
      alert('❌ Failed to send: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSendingCampaignV2(false);
    }
  }, [sendConfirmPhrase, fetchCampaignsV2]);
  
  // Poll campaign status
  const pollCampaignStatus = useCallback((campaignId) => {
    let pollCount = 0;
    const maxPolls = 40;
    
    const interval = setInterval(async () => {
      pollCount++;
      try {
        const response = await axios.get(`${API_URL}/api/email-v2/campaigns/${campaignId}`);
        const status = response.data.status;
        
        if (status === 'sent' || status === 'failed' || pollCount >= maxPolls) {
          clearInterval(interval);
          fetchCampaignsV2();
        }
      } catch (err) {
        console.error('Error polling status:', err);
        clearInterval(interval);
      }
    }, 3000);
  }, [fetchCampaignsV2]);
  
  // View analytics
  const handleViewAnalyticsV2 = useCallback((campaignId) => {
    setSelectedCampaignId(campaignId);
    setCampaignV2View('analytics');
  }, []);
  
  // Add to suppression list
  const handleAddSuppression = useCallback(async (email, reason = 'manual', notes = '') => {
    try {
      await axios.post(`${API_URL}/api/email-v2/suppression`, {
        email,
        reason,
        notes
      });
      await fetchSuppressionList();
    } catch (err) {
      console.error('Error adding to suppression:', err);
      alert('Failed to add: ' + (err.response?.data?.detail || err.message));
    }
  }, [fetchSuppressionList]);
  
  // Remove from suppression
  const handleRemoveSuppression = useCallback(async (suppressionId) => {
    try {
      await axios.delete(`${API_URL}/api/email-v2/suppression/${suppressionId}`);
      setSuppressionList(prev => prev.filter(s => s.id !== suppressionId));
    } catch (err) {
      console.error('Error removing from suppression:', err);
    }
  }, []);
  
  // Back to list
  const handleBackToList = useCallback(() => {
    setSelectedCampaignId(null);
    setCampaignV2View('list');
  }, []);
  
  return {
    // State
    campaignsV2,
    loadingCampaignsV2,
    selectedCampaignId,
    campaignV2View,
    suppressionList,
    suppressionStats,
    sendingCampaignV2,
    sendConfirmPhrase,
    
    // Setters
    setCampaignV2View,
    setSelectedCampaignId,
    setSendConfirmPhrase,
    
    // Actions
    loadCampaignsV2Data,
    fetchCampaignsV2,
    fetchSuppressionList,
    handleCreateCampaignV2,
    handleEditCampaignV2,
    handleDeleteCampaignV2,
    handleReviewCampaignV2,
    handleSendCampaignV2,
    handleViewAnalyticsV2,
    handleAddSuppression,
    handleRemoveSuppression,
    handleBackToList,
  };
};

export default useCampaignsV2State;
